import { Flex, Paper, Stack, Switch, Title } from "@mantine/core";
import { IGridProbabilityWithId, IIssueBoardApi, IRisikoProbability } from "@mcapps/shared";
import { ColDef } from "ag-grid-community";
import React from "react";
import {
  openapi,
  usePostProjectByProjectIdIssueboardRisikoAxisorientationUpdateMutation,
  usePostProjectByProjectIdIssueboardRisikoProbabilityIsactiveaftertiltakMutation,
  usePostProjectByProjectIdIssueboardRisikoProbabilityUpdateMutation,
} from "../../../../../../openapi";
import { FailedAlert } from "../../../../../components";
import ViewEditTable from "../../../../../components/ViewEditTable/ViewEditTable";
import { VETEmptyValidator } from "../../../../../components/ViewEditTable/ViewEditTableValidators";
import { useAppDispatch } from "../../../../../hooks";

const RisikoProbabilitySettings: React.FC<{ issueBoard: IIssueBoardApi }> = ({ issueBoard }) => {
  const dispatch = useAppDispatch();
  const [setProbabilityActive, { error: activateProbabilityError }] =
    usePostProjectByProjectIdIssueboardRisikoProbabilityIsactiveaftertiltakMutation();
  const [updateProbabilities, { error: updateProbabilitiesError }] =
    usePostProjectByProjectIdIssueboardRisikoProbabilityUpdateMutation();
  const [updateAxisOrientation, { error: updateAxisOrientationError }] =
    usePostProjectByProjectIdIssueboardRisikoAxisorientationUpdateMutation();

  const colSettings: ColDef<IGridProbabilityWithId>[] = [
    {
      field: "id",
      headerName: "ID",
      width: 70,
    },
    {
      field: "name",
      headerName: "Navn",
      editable: true,
    },
    {
      field: "description",
      headerName: "Beskrivelse",
      editable: true,
    },
    {
      field: "matrixValue",
      cellDataType: "number",
      headerName: "Matriseverdi",
      editable: true,
    },
    /*{
      field: "order",
      headerName: "Order",
      editable: false,
    },*/
    {
      field: "isActive",
      headerName: "Aktiv",
      editable: true,
      cellEditor: "agSelectCellEditor",
      cellEditorParams: {
        values: ["Ja", "Nei"],
      },
    },
  ];

  const template: IGridProbabilityWithId = {
    id: "",
    name: "",
    description: "",
    matrixValue: 1,
    isActive: "Ja",
    order: 0,
  };

  const data: IGridProbabilityWithId[] = Object.entries(issueBoard.risikoSection?.probabilites ?? {}).map(
    ([key, value]) => ({
      ...value,
      id: key,
      // Need to convert boolean to string for ag-grid to not use checkboxes which cannot be disabled when editMode is false
      // Also, since ViewEditTable sets values internally we cant use a custom valueFormatter
      isActive: value.isActive === true ? "Ja" : "Nei",
    }),
  );

  const formatProbabilites = (probabilities: IRisikoProbability[]) => {
    return probabilities.map((p, index) => ({
      ...p,
      order: index,
    }));
  };
  return (
    <Paper withBorder p="sm" radius="sm" bg="#fdfdff">
      <Stack gap="xs">
        <Title order={4} fw={400}>
          Sannsynlighet
        </Title>
        {(activateProbabilityError != null ||
          updateProbabilitiesError != null ||
          updateAxisOrientationError != null) && <FailedAlert />}
        <Stack gap={0}>
          <Flex>
            <p style={{ marginRight: "0.5rem" }}>
              <i className="fa fa-info-circle fa-fw icon-info" />
              Angi sannsynlighet og konsekvens før <b>og</b> etter tiltak
            </p>
            <Switch
              size="sm"
              onChange={(event) =>
                setProbabilityActive({
                  projectId: issueBoard.project,
                  body: {
                    issueBoardId: issueBoard._id,
                    isActive: event.currentTarget.checked,
                  },
                })
              }
              checked={issueBoard.risikoSection?.enableProbabilityAfterTiltak === true}
            />
          </Flex>
          <Flex align="center" mt="xs" mb="xs">
            <p style={{ margin: 0, marginRight: "0.5rem" }}>
              <i className="fa fa-info-circle fa-fw icon-info" />
              Vis konsekvens på x-aksen og sannsynlighet på y-aksen og snu om på verdiene
            </p>
            <Switch
              size="sm"
              checked={issueBoard.risikoSection?.consequenceOnXAxis === true}
              onChange={async (event) => {
                const consequenceOnXAxis = event.currentTarget.checked;
                try {
                  await updateAxisOrientation({
                    projectId: issueBoard.project,
                    body: {
                      issueBoardId: issueBoard._id,
                      consequenceOnXAxis,
                    },
                  }).unwrap();

                  dispatch(
                    openapi.util.updateQueryData(
                      "getProjectByProjectIdIssueboardAndIssueBoardId",
                      { projectId: issueBoard.project, issueBoardId: issueBoard._id },
                      (draft) => {
                        if (draft.risikoSection != null) {
                          (draft.risikoSection as any).consequenceOnXAxis = consequenceOnXAxis;
                        }
                      },
                    ),
                  );
                } catch {
                  // The mutation result exposes the error through updateAxisOrientationError.
                }
              }}
            />
          </Flex>
          <ViewEditTable
            columns={colSettings}
            validators={[null, VETEmptyValidator, null, VETEmptyValidator]}
            data={data}
            newItemTemplateObject={template}
            onSave={(data) => {
              //convert isActive to boolean
              const newData = data.map((item) => ({
                ...item,
                isActive: item.isActive === "Ja",
              }));
              updateProbabilities({
                projectId: issueBoard.project,
                body: {
                  issueBoardId: issueBoard._id,
                  probabilities: formatProbabilites(newData) as any,
                },
              });
            }}
            canEdit={issueBoard.risikoSection?.isActive === true}
            canRemovePredicate={(item) => item.id === ""}
            orderProp="order"
          />
        </Stack>
      </Stack>
    </Paper>
  );
};

export default RisikoProbabilitySettings;
